import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject, combineLatest } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";
import {
  GuideCompletionEvent,
  GuideCompletionReason,
  GuideDefinition,
  GuideStep,
  IGuideStartOptions,
} from "./guide-step.model";

/**
 * Motor de estado de um guia de navegação (guided tour): controla qual guia está ativo, o passo
 * atual e a sequência entre passos. Não conhece Router, permissões nem overlay — essas integrações
 * são recebidas como funções opcionais em `start()` (`IGuideStartOptions`), para que as tasks que
 * cuidam de navegação entre rotas e de permissões por perfil não precisem alterar esta classe.
 *
 * Só um guia fica ativo por vez: chamar `start()` com um novo guia substitui o guia anterior sem
 * misturar estado entre eles.
 */
@Injectable({
  providedIn: "root",
})
export class GuideService {
  private readonly activeGuideSubject = new BehaviorSubject<GuideDefinition | null>(null);
  private readonly currentStepIndexSubject = new BehaviorSubject<number>(-1);
  private readonly completedSubject = new Subject<GuideCompletionEvent>();
  private options: IGuideStartOptions = {};

  readonly currentStep$: Observable<GuideStep | null> = combineLatest([
    this.activeGuideSubject,
    this.currentStepIndexSubject,
  ]).pipe(
    map(([guide, index]) => guide?.steps[index] ?? null),
    distinctUntilChanged(),
  );

  readonly guideCompleted$: Observable<GuideCompletionEvent> = this.completedSubject.asObservable();

  constructor() {
    // Centraliza o gancho de navegação aqui: quem chamar start() com `onNavigate` não precisa
    // assinar currentStep$ por conta própria para reagir a cada passo.
    this.currentStep$.subscribe((step) => {
      if (step) {
        this.options.onNavigate?.(step);
      }
    });
  }

  get isActive(): boolean {
    return this.activeGuideSubject.value !== null;
  }

  /**
   * Inicia um guia. Os passos são filtrados uma única vez aqui (por `canShowStep`, por
   * `targetSelector` vazio e por seletor CSS malformado) — nem `next()`/`previous()` nem os
   * consumidores do overlay precisam repetir essa checagem depois. Se a filtragem não deixar
   * nenhum passo, o guia não chega a iniciar: emite `guideCompleted$` com `reason: "skipped"` direto.
   */
  start(guide: GuideDefinition, options: IGuideStartOptions = {}): void {
    this.options = options;

    const visibleSteps = options.canShowStep ? guide.steps.filter(options.canShowStep) : guide.steps;
    const runnableSteps = visibleSteps.filter(
      (step) => !!step.targetSelector && this.isValidTargetSelector(step.targetSelector),
    );

    if (!runnableSteps.length) {
      this.options = {};
      this.completedSubject.next({ guideId: guide.id, reason: "skipped" });
      return;
    }

    this.activeGuideSubject.next({ ...guide, steps: runnableSteps });
    this.currentStepIndexSubject.next(0);
  }

  next(): void {
    const guide = this.activeGuideSubject.value;
    if (!guide) {
      return;
    }
    const nextIndex = this.currentStepIndexSubject.value + 1;
    if (nextIndex >= guide.steps.length) {
      const onExhausted = this.options.onExhausted;
      if (onExhausted) {
        this.activeGuideSubject.next(null);
        this.currentStepIndexSubject.next(-1);
        this.options = {};
        onExhausted();
        return;
      }
      this.finish("completed");
      return;
    }
    this.currentStepIndexSubject.next(nextIndex);
  }

  previous(): void {
    if (!this.activeGuideSubject.value) {
      return;
    }
    const previousIndex = Math.max(0, this.currentStepIndexSubject.value - 1);
    this.currentStepIndexSubject.next(previousIndex);
  }

  skip(): void {
    this.finish("skipped");
  }

  finish(reason: GuideCompletionReason = "completed"): void {
    const guide = this.activeGuideSubject.value;
    if (!guide) {
      return;
    }
    this.activeGuideSubject.next(null);
    this.currentStepIndexSubject.next(-1);
    this.options = {};
    this.completedSubject.next({ guideId: guide.id, reason });
  }

  /** `querySelector` lança em CSS malformado (ex.: `">>>"`). Seletor vazio já foi filtrado antes. */
  private isValidTargetSelector(selector: string): boolean {
    try {
      document.querySelector(selector);
      return true;
    } catch {
      return false;
    }
  }
}
